/**
 * Keyboard bindings, declared as data.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS AS A LAYER
 * ---------------------------------------------------------------------------
 *
 * An operator pricing thirty accommodations across a dozen periods is doing
 * data entry, and data entry is a keyboard activity. A grid that needs the
 * mouse between cells is slower than the spreadsheet they will use instead —
 * and once they are back in the spreadsheet, the tool has lost.
 *
 * Shortcuts bolted on later end up as `onKeyDown` handlers scattered through
 * components: undiscoverable, inconsistent between screens, and impossible to
 * list. So bindings are DATA registered by scope, one document listener
 * dispatches them, and the help overlay is generated from the same registry.
 * A binding that exists is therefore always documented, by construction.
 *
 * ---------------------------------------------------------------------------
 * COMBOS
 * ---------------------------------------------------------------------------
 *
 * `mod` means Ctrl on Windows/Linux and Cmd on macOS, which is the only
 * portable way to say "the copy/paste modifier".
 *
 *   'mod+d'  'shift+enter'  'arrowdown'  'escape'  '?'
 */

import {
  createContext, useContext, useEffect, useId, useMemo, useRef, useState,
  type ReactNode,
} from 'react';
import { useT, type MessageKey } from './i18n';

export interface Binding {
  /** e.g. `mod+d`, `shift+enter`, `arrowright`. */
  combo: string;
  /**
   * Shown in the help overlay. Imperative, e.g. "Fill down".
   *
   * A catalogue key rather than a sentence, so bindings stay declarative data
   * and the overlay — which already generates itself from the registry — is the
   * single place that turns them into words. A binding declared in a component
   * that re-registers on every keystroke must not drag a translation function
   * through its dependency array to do it.
   */
  label: MessageKey;
  /** Heading in the overlay. */
  group?: MessageKey;
  run: (e: KeyboardEvent) => void;
  /** Skip the binding without unregistering it, so it stays documented. */
  when?: () => boolean;
  /**
   * Fire even while a text field has focus.
   *
   * Off by default: a bare `d` must type a `d`, not run a command. Grid cells
   * are inputs, so their navigation keys set this.
   */
  inFields?: boolean;
  /** Leave the browser default alone (rare; most bindings replace it). */
  passive?: boolean;
}

interface Registration { id: string; bindings: Binding[] }

interface KeyMapApi {
  register(id: string, bindings: Binding[]): void;
  unregister(id: string): void;
  all(): Binding[];
}

const KeyMapContext = createContext<KeyMapApi | null>(null);

const IS_MAC = typeof navigator !== 'undefined' && /mac|iphone|ipad/i.test(navigator.platform || '');

/** Canonical form of a keyboard event, to match against a combo string. */
export function comboOf(e: KeyboardEvent): string {
  const parts: string[] = [];
  if (e.ctrlKey || e.metaKey) parts.push('mod');
  if (e.altKey) parts.push('alt');
  if (e.shiftKey) parts.push('shift');
  const key = e.key.length === 1 ? e.key.toLowerCase() : e.key.toLowerCase();
  parts.push(key);
  return parts.join('+');
}

/** Human-readable combo, for the overlay. */
export function prettyCombo(combo: string): string {
  return combo
    .split('+')
    .map((p) => {
      if (p === 'mod') return IS_MAC ? '⌘' : 'Ctrl';
      if (p === 'shift') return IS_MAC ? '⇧' : 'Shift';
      if (p === 'alt') return IS_MAC ? '⌥' : 'Alt';
      if (p === 'arrowup') return '↑';
      if (p === 'arrowdown') return '↓';
      if (p === 'arrowleft') return '←';
      if (p === 'arrowright') return '→';
      if (p === 'enter') return 'Enter';
      if (p === 'escape') return 'Esc';
      if (p === 'tab') return 'Tab';
      return p.length === 1 ? p.toUpperCase() : p;
    })
    .join(IS_MAC ? '' : '+');
}

function isTextField(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

export function KeyMapProvider({ children }: { children: ReactNode }) {
  const regs = useRef<Registration[]>([]);
  const [, bump] = useState(0);

  const api = useMemo<KeyMapApi>(() => ({
    register(id, bindings) {
      const i = regs.current.findIndex((r) => r.id === id);
      if (i >= 0) regs.current[i] = { id, bindings };
      else regs.current.push({ id, bindings });
      bump((n) => n + 1);
    },
    unregister(id) {
      regs.current = regs.current.filter((r) => r.id !== id);
      bump((n) => n + 1);
    },
    // Later registrations win, so a focused grid can override a global binding
    // without either side knowing about the other.
    all() { return regs.current.flatMap((r) => r.bindings).reverse(); },
  }), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const combo = comboOf(e);
      const inField = isTextField(e.target);
      for (const b of api.all()) {
        if (b.combo !== combo) continue;
        if (inField && !b.inFields) continue;
        if (b.when && !b.when()) continue;
        if (!b.passive) { e.preventDefault(); e.stopPropagation(); }
        b.run(e);
        return;
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [api]);

  return <KeyMapContext.Provider value={api}>{children}</KeyMapContext.Provider>;
}

/**
 * Register bindings for as long as the component is mounted.
 *
 * The array is re-registered whenever it changes, so closures stay fresh
 * without every binding needing a ref.
 */
export function useKeyMap(bindings: Binding[]): void {
  const api = useContext(KeyMapContext);
  const id = useId();
  useEffect(() => {
    if (!api) return undefined;
    api.register(id, bindings);
    return () => api.unregister(id);
  }, [api, id, bindings]);
}

/** Everything currently bound, grouped, for the help overlay. */
export function useAllBindings(): Array<[MessageKey, Binding[]]> {
  const api = useContext(KeyMapContext);
  const list = api ? api.all() : [];
  const groups = new Map<MessageKey, Binding[]>();
  const seen = new Set<string>();
  for (const b of list) {
    const k = `${b.group ?? 'keys.groupGeneral'}|${b.combo}`;
    if (seen.has(k)) continue; // a shadowed binding is listed once, by its winner
    seen.add(k);
    const g = b.group ?? 'keys.groupGeneral';
    const arr = groups.get(g);
    if (arr) arr.push(b); else groups.set(g, [b]);
  }
  return [...groups.entries()];
}

/**
 * The help overlay.
 *
 * Generated from the live registry rather than written by hand, so it cannot
 * drift out of date: a binding that exists is listed, and one that has been
 * removed disappears.
 */
export function ShortcutHelp({ open, onClose }: { open: boolean; onClose: () => void }) {
  const groups = useAllBindings();
  const t = useT();
  if (!open) return null;
  return (
    <div
      className="overlay"
      role="dialog"
      aria-modal="true"
      aria-label={t('keys.title')}
      onClick={onClose}
    >
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-hd">
          <h2>{t('keys.title')}</h2>
          <span className="grow" />
          <button type="button" className="btn" onClick={onClose}>{t('keys.close')}</button>
        </div>
        <div className="sheet-bd keys">
          {groups.map(([group, bindings]) => (
            <section key={group}>
              <h3 className="eyebrow">{t(group)}</h3>
              <dl>
                {bindings.map((b) => (
                  <div className="kb" key={b.combo + b.label}>
                    <dt><kbd>{prettyCombo(b.combo)}</kbd></dt>
                    <dd>{t(b.label)}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
